import { stateHandler } from './state';
import { Platforms } from '@enums';
import { FloatingWidgetConfig } from '@interfaces';
import '@shared/components/global-modal';
import { initMixpanel } from '@utils/events';
import { initializeWidgets } from './placement';
import { getGlobalModalData } from '../../../lib/serializers/global-modal';
import { DebugLogger } from '@inspiredmember/debug-logger';
import { ShopifyPlatformModule } from './platform/shopify/standard';
import { HeadlessPlatformModule } from './platform/shopify/headless';
import getDeviceFingerprint from '@utils/device-fingerprint';
import { toggleGlobalModal } from '@utils/helper-functions';

(window as any).Inspired = {
  ...(window as any).Inspired || {},
  toggleGlobalModal
};

interface GlobalModalElement extends HTMLElement {
  impactOffer: any;
  donationString: string;
}

const staticCacheUrl = import.meta.env.VITE_STATIC_CACHE_URL;
const inspiredGlobal = window as any;
let isPreview = false;
const logger = DebugLogger.getInstance();

// initialize data promise, how the widgets wait for their data
inspiredGlobal.inspiredDataPromise = Promise.all([
  new Promise(resolve => {
    inspiredGlobal.resolveInspiredPlatformModule = resolve; // platform module
  }),
  new Promise(resolve => {
    inspiredGlobal.resolveInspiredStateHandler = resolve; // state handler
  })
]);

async function setup() {
  // check if in preview mode
  const adminPreview = inspiredGlobal.adminPreview;
  const isEditor =
    inspiredGlobal?.Shopify?.designMode || window.location.pathname.includes('editor');
  await handlePreviewMode();

  let platformModule: ShopifyPlatformModule | HeadlessPlatformModule | null = null;
  if (inspiredGlobal.Inspired?.platform) {
    // dynamically import the platform-specific module
    platformModule = await importPlatformModule(inspiredGlobal.Inspired.platform);
  }
  if (!platformModule) {
    logger.error('No Inspired platform module found');
    return;
  }

  let platformStoreId = '';
  if (inspiredGlobal.Inspired?.storeId) {
    platformStoreId = inspiredGlobal.Inspired.storeId;
  } else {
    Array.from(document.scripts).forEach(script => {
      if (script.src.includes('widgetloader.js')) {
        const params = new URLSearchParams(script.src.split('?')[1]);
        const storeId = params.get('psid');
        if (storeId) platformStoreId = storeId;
      }
    });
  }
  inspiredGlobal.inspiredStoreId = platformStoreId;

  const data = await getData(platformStoreId);

  const { donationType, donationAmount } = data.campaign;
  const donationString = `${donationType === 'fixed' ? '$' : ''}${donationAmount}${donationType === 'percentage' ? '%' : ''
    }`;

  function handleAdminPreviewChange() {
    const data = inspiredGlobal.adminPreview?.customization;
    if (!data) {
      console.warn('No adminPreview.customization found');
      return;
    }

    console.log('adminPreview:change event triggered', data);

    stateHandler.setData({
      previewConfig: data
    });
    inspiredGlobal.adminPreview.dataLoaded = true;
  }

  window.addEventListener('adminPreview:change', handleAdminPreviewChange);
  if (!adminPreview && !data.campaign.active && !isPreview && !isEditor) {
    logger.debug(
      'campaign is not active, not in the editor or preview mode, not initializing widgets.'
    );
    return;
  }

  stateHandler.setData({
    storeName: data.store.name,
    campaign: data.campaign,
    donationPercent: donationType === 'percentage' ? donationAmount : null,
    donationString: donationString,
    causes: data.causes.available,
    selectedCause: data.causes.featured,
    bannerConfig: data.customization.banner,
    floatingConfig: data.customization.floating as FloatingWidgetConfig,
    pageSectionConfig: data.customization.pageSection,
    progressCartConfig: data.customization.progressCart,
    progressCartDrawerConfig: data.customization.progressCartDrawer,
    embeddedProductConfig: data.customization.embeddedProduct,
    embeddedCartConfig: data.customization.embeddedCart,
    embeddedCartDrawerConfig: data.customization.embeddedCartDrawer,
    isAdminPreview: !!adminPreview,
    donationFormat: data.store.donationFormat
  });

  // initialize analytics if not in preview mode
  if (!isPreview && !isEditor) {
    try {
      // identify the user
      const deviceId = await getDeviceFingerprint();
      if (deviceId) {
        inspiredGlobal.inspiredMixpanel = initMixpanel(deviceId);
        // const isHeadless = inspiredGlobal.Inspired?.platform === Platforms.ShopifyHeadless;
        sessionStorage.setItem('inspired-device-id', deviceId);

        // set cart attributes
        await platformModule.persistTracking(deviceId);

        // provides a public window function that allows tracking to be saved to the platform. (cart attributes for shopify)
        inspiredGlobal.inspiredPersistTracking = async () => {
          await platformModule.persistTracking(deviceId);
        }
      }
    }
    catch (e) {
      logger.error('Failed to initialize tracking', e);
    }
  }

  // store-specific checks
  if (decodeURIComponent(platformStoreId) === 'O5CFSln/nCru0no9A9A59w==')
    inspiredGlobal.isFastFood = true;

  // append global modal
  const globalModalElement = document.createElement('inspired-global-modal') as GlobalModalElement;
  globalModalElement.impactOffer = getGlobalModalData(data, donationString);

  document.body.appendChild(globalModalElement);

  // dynamically import active widget modules
  await importActiveWidgets(data, adminPreview);

  // resolve the platformModule and state promise
  inspiredGlobal.inspiredPlatformModule = platformModule;
  inspiredGlobal.resolveInspiredPlatformModule(platformModule);
  inspiredGlobal.inspiredStateHandler = stateHandler;
  inspiredGlobal.resolveInspiredStateHandler(stateHandler);

  // inside shopify admin, don't need to run placement logic
  if (adminPreview) {
    inspiredGlobal.adminPreview.dataLoaded = true;
    return;
  }

  initializeWidgets(data);

  // emit loaded event
  window.dispatchEvent(new CustomEvent("Inspired::Loaded"));
}

async function importPlatformModule(platformName: Platforms) {
  switch (platformName) {
    case Platforms.Shopify:
      return (await import('./platform/shopify/standard')).default;
    case Platforms.ShopifyHeadless:
      return (await import('./platform/shopify/headless')).default;
    default:
      throw new Error(`Unknown platform: ${platformName}`);
  }
}

async function importActiveWidgets(data: any, adminPreview: boolean) {
  const widgetImports = [];
  const widgets = data.customization;

  if (adminPreview || (widgets.embeddedCart?.active || widgets.embeddedCartDrawer?.active || widgets.embeddedProduct?.active)) {
    widgetImports.push(import('./widgets/embedded-widget'));
  }
  if (adminPreview || widgets.pageSection?.active) {
    widgetImports.push(import('./widgets/page-section-widget'));
  }
  if (adminPreview || widgets.banner?.active) {
    widgetImports.push(import('./widgets/banner-widget'));
  }
  if (adminPreview || widgets.floating?.active) {
    widgetImports.push(import('./widgets/floating-widget'));
  }
  if (adminPreview || (widgets.progressCart?.active || widgets.progressCartDrawer?.active)) {
    widgetImports.push(import('./widgets/cart-progress-widget'));
  }

  await Promise.all(widgetImports);
}

async function getData(storeId: string) {
  const url =
    inspiredGlobal.Inspired?.storeUrl ||
    inspiredGlobal.Shopify?.shop ||
    inspiredGlobal?.adminPreview?.myshopifyDomain;
  const shopName = encodeURIComponent(url?.replace('.myshopify.com', ''));
  const response = await fetch(`${staticCacheUrl}/stores/${shopName}/configv2.json`, {
    mode: 'cors'
  });
  const data = await response.json();
  return data;
}

async function handlePreviewMode() {
  const searchParams = new URLSearchParams(window.location.search);
  const currentTime = Date.now();
  const urlPreview = searchParams.get('inspired-preview') === 'true';

  const storedPreviewData = sessionStorage.getItem('__inspired_preview__');
  const parsedPreviewData = storedPreviewData ? JSON.parse(storedPreviewData) : null;

  // check if session storage preview is less than 15 minutes old
  const isStoredPreviewValid =
    // !!parsedPreviewData &&
    // parsedPreviewData.isPreview === true &&
    // !!parsedPreviewData.previewType &&
    parsedPreviewData &&
    currentTime - parsedPreviewData.timestamp < 15 * 60 * 1000;

  isPreview = urlPreview || isStoredPreviewValid;

  // remove impact-offer data from when preview was active
  if (!!parsedPreviewData && !isStoredPreviewValid) {
    sessionStorage.removeItem('__inspired_preview__');
  }

  if (isPreview) {
    if (!isStoredPreviewValid) {
      const previewData = {
        isPreview: true,
        previewType: searchParams.get('inspired-preview-type') ?? '',
        timestamp: currentTime
      };
      sessionStorage.setItem('__inspired_preview__', JSON.stringify(previewData));
    }
    checkForPreviewRedirect(isPreview);
  }
}

function checkForPreviewRedirect(isPreview: boolean) {
  const searchParams = new URLSearchParams(window.location.search);
  const previewRedirect = searchParams.get('inspired-redirect') === 'true';
  const productId = searchParams.get('id');
  if (isPreview && previewRedirect && productId) {
    const baseUrl = window.location.origin;
    // this url will add a product to their cart so that they can see the widget
    const fullUrl = `${baseUrl}/cart/add?id=${productId}&quantity=1`;
    window.location.href = fullUrl;
  }
}

setup();
if (inspiredGlobal.adminPreview) inspiredGlobal.inspiredRefreshData = setup;
